import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { hasRole } from '../utils';
import * as hr from './api';
import ConfirmDialog from './components/ConfirmDialog';

const Ctx = createContext(null);
export const useHR = () => useContext(Ctx);

// HR writes are limited to these roles on the backend (require_roles in the HR endpoints);
// super_admin is implied by hasRole.
const HR_MANAGERS = ['md', 'dmd'];

// shared lookups, loaded once and kept until a screen changes the underlying data
const LOOKUPS = {
  employees: () => hr.listEmployees(),
  departments: () => hr.listDepartments(),
  sites: () => hr.listSites(),
  designations: () => hr.listDesignations(),
  branches: () => hr.listBranches().catch(() => []),
  leaveTypes: () => hr.listLeaveTypes(),
  accommodations: () => hr.listAccommodations(),
};
const cache = {};
export const loadLookup = key => {
  if (!cache[key]) cache[key] = LOOKUPS[key]().catch(e => { delete cache[key]; throw e; });
  return cache[key];
};
export const clearLookup = (...keys) => keys.forEach(k => delete cache[k]);

// options for a select: [{ value, label }]
const LABELS = {
  employees: e => `${e.full_name} (${e.employee_no})`,
  departments: d => d.name,
  sites: s => s.name,
  designations: d => d.title,
  branches: b => b.name,
  leaveTypes: t => t.name,
  accommodations: a => a.property_name,
};
export function useLookup(key) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    loadLookup(key).then(r => live && setRows(r)).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [key]);
  return rows;
}
export function useOptions(key) {
  const rows = useLookup(key);
  return useMemo(() => (rows || []).map(r => ({ value: String(r.id), label: LABELS[key](r), row: r })), [rows, key]);
}

export function HRProvider({ user, toast, children }) {
  const canManage = hasRole(user, HR_MANAGERS);
  const isSuperAdmin = hasRole(user, ['super_admin']);
  const notify = useCallback((message, tone = 'good') => {
    toast?.show?.(message, tone === 'bad' ? 'error' : tone === 'warn' ? 'info' : 'success');
  }, [toast]);
  // confirm({ title, message, detail, confirmLabel, danger }) -> Promise<boolean>, shown in the HR confirm modal
  const [asking, setAsking] = useState(null);
  const confirm = useCallback(opts => new Promise(resolve => setAsking({ ...(typeof opts === 'string' ? { message: opts } : opts), resolve })), []);
  const answer = ok => { asking?.resolve(ok); setAsking(null); };
  const value = useMemo(() => ({ user, canManage, isSuperAdmin, notify, confirm }), [user, canManage, isSuperAdmin, notify, confirm]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {asking && <ConfirmDialog {...asking} onAnswer={answer} />}
    </Ctx.Provider>
  );
}
